import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Mail, Building, User, Calendar, FileText, LogOut, MessageSquare, Send, UserPlus, Key } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { getDemoRequests, getDocumentationRequests, getNotes, createNote, inviteAdmin, changePassword } from '../api/emboscale';

const AdminDashboard = () => {
  const [demoRequests, setDemoRequests] = useState([]);
  const [docRequests, setDocRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [notes, setNotes] = useState({});
  const [newNote, setNewNote] = useState('');
  const [activeRequest, setActiveRequest] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [demos, docs] = await Promise.all([
        getDemoRequests(),
        getDocumentationRequests()
      ]);
      setDemoRequests(demos);
      setDocRequests(docs);
      toast.success('Data refreshed successfully');
    } catch (error) {
      toast.error('Failed to fetch data');
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchNotes = async (requestId) => {
    try {
      const data = await getNotes(requestId);
      setNotes(prev => ({ ...prev, [requestId]: data }));
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  const addNote = async (requestId, requestType) => {
    if (!newNote.trim()) return;
    
    try {
      await createNote(requestId, requestType, newNote);
      
      toast.success('Note added successfully');
      setNewNote('');
      await fetchNotes(requestId);
    } catch (error) {
      toast.error('Failed to add note');
      console.error('Error adding note:', error);
    }
  };

  const handleInviteAdmin = async (e) => {
    e.preventDefault();
    setIsInviting(true);
    
    try {
      const response = await inviteAdmin(inviteEmail, inviteName);
      
      toast.success(`Admin invited! Temporary password: ${response.admin.temp_password}`, {
        duration: 10000
      });
      
      setInviteEmail('');
      setInviteName('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite admin');
    } finally {
      setIsInviting(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }
    
    setIsChangingPassword(true);
    
    try {
      await changePassword(currentPassword, newPassword);
      
      toast.success('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Close dialog
      document.querySelector('[data-state="open"]')?.click();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
    toast.success('Logged out successfully');
  };

  useEffect(() => {
    fetchData();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="bg-gradient-to-r from-gray-900 to-black border-b border-white/10 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Admin Dashboard
              </h1>
              <p className="text-gray-400">Welcome, {user?.email}</p>
            </div>
            <div className="flex items-center gap-3">
              {/* Change Password Dialog */}
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10">
                    <Key className="mr-2" size={18} />
                    Change Password
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-gray-900 border-white/10">
                  <DialogHeader>
                    <DialogTitle className="text-white">Change Password</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleChangePassword} className="space-y-4">
                    <div>
                      <Input
                        type="password"
                        placeholder="Current Password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        required
                        className="bg-black/50 border-white/20 text-white"
                      />
                    </div>
                    <div>
                      <Input
                        type="password"
                        placeholder="New Password (min 8 characters)"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength={8}
                        className="bg-black/50 border-white/20 text-white"
                      />
                    </div>
                    <div>
                      <Input
                        type="password"
                        placeholder="Confirm New Password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        className="bg-black/50 border-white/20 text-white"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isChangingPassword}
                      className="w-full bg-cyan-600 hover:bg-cyan-700"
                    >
                      {isChangingPassword ? 'Changing...' : 'Change Password'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Invite Admin Dialog */}
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10">
                    <UserPlus className="mr-2" size={18} />
                    Invite Admin
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-gray-900 border-white/10">
                  <DialogHeader>
                    <DialogTitle className="text-white">Invite New Admin</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleInviteAdmin} className="space-y-4">
                    <div>
                      <Input
                        type="email"
                        placeholder="Email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        required
                        className="bg-black/50 border-white/20 text-white"
                      />
                    </div>
                    <div>
                      <Input
                        type="text"
                        placeholder="Name (optional)"
                        value={inviteName}
                        onChange={(e) => setInviteName(e.target.value)}
                        className="bg-black/50 border-white/20 text-white"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isInviting}
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                    >
                      {isInviting ? 'Inviting...' : 'Send Invite'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              <Button
                onClick={fetchData}
                disabled={isLoading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <RefreshCw className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} size={18} />
                Refresh
              </Button>

              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              >
                <LogOut className="mr-2" size={18} />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <Card className="bg-gradient-to-br from-emerald-900/20 to-black/80 backdrop-blur-md border border-emerald-500/30">
            <CardHeader>
              <CardTitle className="text-emerald-400 flex items-center gap-2">
                <FileText size={24} />
                Demo Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-5xl font-bold text-white">
                {demoRequests.length}
              </div>
              <p className="text-gray-400 mt-2">Total submissions</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-cyan-900/20 to-black/80 backdrop-blur-md border border-cyan-500/30">
            <CardHeader>
              <CardTitle className="text-cyan-400 flex items-center gap-2">
                <Mail size={24} />
                Documentation Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-5xl font-bold text-white">
                {docRequests.length}
              </div>
              <p className="text-gray-400 mt-2">Total submissions</p>
            </CardContent>
          </Card>
        </div>

        {/* Demo Requests */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <FileText size={24} className="text-emerald-400" />
            Demo Requests
          </h2>
          
          {demoRequests.length === 0 ? (
            <Card className="bg-gray-900/50 border-white/10">
              <CardContent className="py-12 text-center text-gray-400">
                No demo requests yet
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {demoRequests.map((request) => (
                <Card
                  key={request.id}
                  className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-md border border-white/10 hover:border-emerald-500/50 transition-all duration-300"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <h3 className="text-xl font-bold text-white">
                            {request.name}
                          </h3>
                          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                            {request.status}
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div className="flex items-center gap-2 text-gray-400">
                            <Mail size={16} />
                            <span>{request.email}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-400">
                            <Building size={16} />
                            <span>{request.company}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-400">
                            <Calendar size={16} />
                            <span>{formatDate(request.submitted_at)}</span>
                          </div>
                        </div>

                        {request.message && (
                          <div className="mb-4 p-4 bg-black/50 rounded-lg border border-white/5">
                            <p className="text-sm text-gray-400 font-semibold mb-2">Message:</p>
                            <p className="text-gray-300">{request.message}</p>
                          </div>
                        )}

                        {/* Notes Section */}
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                              onClick={() => {
                                setActiveRequest({ id: request.id, type: 'demo' });
                                fetchNotes(request.id);
                              }}
                            >
                              <MessageSquare className="mr-2" size={16} />
                              Add Note ({notes[request.id]?.length || 0})
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="bg-gray-900 border-white/10 max-w-2xl">
                            <DialogHeader>
                              <DialogTitle className="text-white">Notes for {request.name}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                              {/* Existing Notes */}
                              {notes[request.id]?.length > 0 && (
                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                  {notes[request.id].map((note) => (
                                    <div key={note.id} className="p-3 bg-black/50 rounded-lg border border-white/5">
                                      <p className="text-gray-300 mb-2">{note.content}</p>
                                      <div className="flex items-center gap-2 text-xs text-gray-500">
                                        <User size={12} />
                                        <span>{note.admin_email}</span>
                                        <span>•</span>
                                        <span>{formatDate(note.created_at)}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              
                              {/* Add New Note */}
                              <div className="space-y-2">
                                <Textarea
                                  placeholder="Add a note..."
                                  value={newNote}
                                  onChange={(e) => setNewNote(e.target.value)}
                                  className="bg-black/50 border-white/20 text-white"
                                  rows={3}
                                />
                                <Button
                                  onClick={() => addNote(request.id, 'demo')}
                                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                                >
                                  <Send className="mr-2" size={16} />
                                  Add Note
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Documentation Requests */}
        <div>
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <Mail size={24} className="text-cyan-400" />
            Documentation Requests
          </h2>
          
          {docRequests.length === 0 ? (
            <Card className="bg-gray-900/50 border-white/10">
              <CardContent className="py-12 text-center text-gray-400">
                No documentation requests yet
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {docRequests.map((request) => (
                <Card
                  key={request.id}
                  className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-md border border-white/10 hover:border-cyan-500/50 transition-all duration-300"
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center">
                          <Mail className="text-cyan-400" size={24} />
                        </div>
                        <div className="flex-1">
                          <p className="text-lg font-semibold text-white">
                            {request.email}
                          </p>
                          <div className="flex items-center gap-2 text-gray-400 text-sm">
                            <Calendar size={14} />
                            <span>{formatDate(request.requested_at)}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Notes for Documentation Requests */}
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                            onClick={() => {
                              setActiveRequest({ id: request.id, type: 'documentation' });
                              fetchNotes(request.id);
                            }}
                          >
                            <MessageSquare className="mr-2" size={16} />
                            Notes ({notes[request.id]?.length || 0})
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-gray-900 border-white/10 max-w-2xl">
                          <DialogHeader>
                            <DialogTitle className="text-white">Notes for {request.email}</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            {notes[request.id]?.length > 0 && (
                              <div className="space-y-2 max-h-64 overflow-y-auto">
                                {notes[request.id].map((note) => (
                                  <div key={note.id} className="p-3 bg-black/50 rounded-lg border border-white/5">
                                    <p className="text-gray-300 mb-2">{note.content}</p>
                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                      <User size={12} />
                                      <span>{note.admin_email}</span>
                                      <span>•</span>
                                      <span>{formatDate(note.created_at)}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            <div className="space-y-2">
                              <Textarea
                                placeholder="Add a note..."
                                value={newNote}
                                onChange={(e) => setNewNote(e.target.value)}
                                className="bg-black/50 border-white/20 text-white"
                                rows={3}
                              />
                              <Button
                                onClick={() => addNote(request.id, 'documentation')}
                                className="w-full bg-cyan-600 hover:bg-cyan-700"
                              >
                                <Send className="mr-2" size={16} />
                                Add Note
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
